import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtomValue } from "jotai";
import { FormEvent, HTMLAttributes, useEffect } from "react";
import {
  showTopNavAtom,
  topNavContainerHeightAtom,
} from "../../../../atoms/navAtoms";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { AutocompleteSuggestion } from "../../../../store/models/autocomplete";
import { NavSearchFilterButton } from "./components/NavSearchFilterButton/NavSearchFilterButton";
import { NavSearchResultsDropDown } from "./components/NavSearchResultsDropDown/NavSearchResultsDropDown";
import "./NavSearchBar.css";

const TEST_PREFIX = "nav-search-bar";

interface NavSearchBarProps extends HTMLAttributes<HTMLInputElement> {
  autocompleteSuggestions: AutocompleteSuggestion[];
  handleSearchQuery: (event: FormEvent<HTMLFormElement>) => void;
  isFetchingAutocomplete: boolean;
  onListItemClick: () => void;
  showResultsDropDown: boolean;
  setShowResultsDropDown: (show: boolean) => void;
  searchTerm?: string;
}

/**
 * Search Bar component made for the Navigation Header.
 * Fetching logic lives in SitewideSearchBar component.
 */
export const NavSearchBar = ({
  autocompleteSuggestions,
  handleSearchQuery,
  isFetchingAutocomplete,
  onBlur,
  onChange,
  onFocus,
  onListItemClick,
  showResultsDropDown,
  setShowResultsDropDown,
  searchTerm,
}: NavSearchBarProps) => {
  const showHeader = useAtomValue(showTopNavAtom);
  const topNavContainerHeight = useAtomValue(topNavContainerHeightAtom);
  const isTooNarrowForDropDown = useMediaQuery("(max-width: 920px)");

  useEffect(() => {
    if (showResultsDropDown && !showHeader) {
      setShowResultsDropDown(false);
    }
  }, [showHeader, showResultsDropDown]);

  const showAttachedDropDown = Boolean(
    showResultsDropDown && searchTerm && !isTooNarrowForDropDown,
  );

  return (
    <>
      <div
        data-testid={`${TEST_PREFIX}-container`}
        className="nav-search-bar-container"
      >
        <form
          className={`nav-search-bar ${showAttachedDropDown ? "show" : ""}`}
          onSubmit={handleSearchQuery}
          data-testid={`${TEST_PREFIX}-form`}
        >
          <input
            className="nav-input"
            name="searchInput"
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            placeholder="Search Recording Studios & Audio Engineers"
            value={searchTerm}
            data-testid={`${TEST_PREFIX}-form-input`}
          />
          <NavSearchFilterButton />
          <button
            className={`nav-search-button ${showAttachedDropDown ? "show" : ""}`}
            name="searchSubmit"
            type="submit"
            data-testid={`${TEST_PREFIX}-form-submit`}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>
        {!isTooNarrowForDropDown && (
          <NavSearchResultsDropDown
            className="nav-search-results-container--attached"
            data={autocompleteSuggestions}
            loading={isFetchingAutocomplete}
            onListItemClick={onListItemClick}
            show={showResultsDropDown}
          />
        )}
      </div>
      {isTooNarrowForDropDown && (
        <NavSearchResultsDropDown
          className="nav-search-results-container--full-width"
          data={autocompleteSuggestions}
          loading={isFetchingAutocomplete}
          onListItemClick={onListItemClick}
          show={showResultsDropDown}
          style={{ top: topNavContainerHeight }}
        />
      )}
    </>
  );
};
